import { useTranslation } from 'react-i18next';
import Navigation from '../../../components/feature/Navigation';
import Footer from '../../../components/feature/Footer';

export default function GazaCrisis2025() {
  const { t } = useTranslation();

  return (
    <div className="min-h-screen bg-white">
      <Navigation />
      
      {/* Hero */}
      <div className="relative h-[60vh] min-h-[500px] flex items-center justify-center">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/743f93bd859bb111954659f0d8c03f2a.jpg"
            alt="Gaza Crisis"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/50 to-black/60"></div>
        </div>
        <div className="relative z-10 max-w-4xl mx-auto px-6 text-center text-white">
          <div className="inline-block px-4 py-2 bg-red-600 rounded-full text-sm font-medium mb-6">
            Emergency Resources • December 3, 2025
          </div>
          <h1 className="text-5xl md:text-6xl font-bold mb-6 leading-tight">
            Gaza: The Crisis the World is Forgetting
          </h1>
          <p className="text-xl md:text-2xl text-gray-200 mb-8">
            No electricity. No internet. No voice. But the suffering continues.
          </p>
          <div className="flex items-center justify-center gap-6 text-sm">
            <span>15 min read</span>
            <span>•</span>
            <span>Updated December 2025</span>
          </div>
        </div>
      </div>

      {/* Personal Note */}
      <div className="max-w-4xl mx-auto px-6 -mt-16 relative z-20 mb-16">
        <div className="bg-gradient-to-br from-red-50 to-pink-50 rounded-2xl p-8 md:p-12 shadow-xl border-l-4 border-red-600">
          <div className="flex items-start gap-4 mb-6">
            <i className="ri-heart-fill text-4xl text-red-600"></i>
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Why SheCan Supports Palestine 🇵🇸</h2>
              <div className="prose prose-lg text-gray-700 space-y-4">
                <p>
                  <strong>The media has moved on. But the crisis hasn't.</strong>
                </p>
                <p>
                  As I write this on December 3, 2025, Gaza is still under siege. Women are still giving birth in tents. Children are still starving. Families are still being killed. But you won't see it on the news anymore.
                </p>
                <p>
                  <strong>Why? Because there's no electricity. No internet. No way for people in Gaza to show the world what's happening to them.</strong>
                </p>
                <p>
                  When the cameras can't film, when journalists can't report, when social media goes dark—the world forgets. But forgetting doesn't stop the bombs. It doesn't feed the hungry children. It doesn't heal the wounded.
                </p>
                <p>
                  <strong>SheCan stands with Palestine because women's rights are human rights everywhere.</strong> We cannot fight for women in one place and ignore them in another. We cannot say "all women matter" and then look away when Palestinian women are suffering.
                </p>
                <p className="text-red-700 font-semibold">
                  This article is our way of keeping Gaza visible. Of refusing to forget. Of demanding the world pay attention. 💔🇵🇸
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <article className="max-w-4xl mx-auto px-6 pb-20">
        
        {/* The Blackout */}
        <section className="mb-16">
          <div className="bg-black text-white rounded-2xl p-8 md:p-12 mb-8">
            <h2 className="text-3xl font-bold mb-6 flex items-center gap-3">
              <i className="ri-signal-wifi-off-line text-4xl"></i>
              The Blackout: Why You're Not Seeing Gaza Anymore
            </h2>
            <div className="space-y-6 text-lg">
              <p>
                <strong>December 2025: Gaza has been without consistent electricity for over 14 months.</strong>
              </p>
              <p>
                No electricity means:
              </p>
              <ul className="space-y-3 ml-6">
                <li className="flex items-start gap-3">
                  <i className="ri-close-circle-fill text-red-500 text-xl mt-1"></i>
                  <span><strong>No internet</strong> - People can't post videos, can't share their stories, can't show the world what's happening</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-close-circle-fill text-red-500 text-xl mt-1"></i>
                  <span><strong>No phones</strong> - Can't charge devices, can't call for help, can't contact family</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-close-circle-fill text-red-500 text-xl mt-1"></i>
                  <span><strong>No media coverage</strong> - Journalists can't file reports, cameras don't work, the world goes dark</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-close-circle-fill text-red-500 text-xl mt-1"></i>
                  <span><strong>No hospitals</strong> - Surgeries by flashlight, incubators shut down, life support fails</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-close-circle-fill text-red-500 text-xl mt-1"></i>
                  <span><strong>No water pumps</strong> - Can't pump clean water, sewage backs up, disease spreads</span>
                </li>
              </ul>
              <p className="text-yellow-300 font-semibold text-xl mt-8">
                When Gaza went dark, the world stopped watching. But the suffering didn't stop.
              </p>
            </div>
          </div>
        </section>

        {/* Current Situation */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold mb-8 text-gray-900">The Reality Right Now (December 2025)</h2>
          
          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-red-50 rounded-xl p-6 border-l-4 border-red-600">
              <div className="flex items-center gap-3 mb-4">
                <i className="ri-skull-line text-3xl text-red-600"></i>
                <h3 className="text-xl font-bold text-gray-900">Death Toll</h3>
              </div>
              <p className="text-4xl font-bold text-red-600 mb-2">50,000+</p>
              <p className="text-gray-700">Palestinians killed since October 2023</p>
              <p className="text-sm text-gray-600 mt-2">70% are women and children</p>
            </div>

            <div className="bg-orange-50 rounded-xl p-6 border-l-4 border-orange-600">
              <div className="flex items-center gap-3 mb-4">
                <i className="ri-home-line text-3xl text-orange-600"></i>
                <h3 className="text-xl font-bold text-gray-900">Displacement</h3>
              </div>
              <p className="text-4xl font-bold text-orange-600 mb-2">1.9 Million</p>
              <p className="text-gray-700">People displaced (85% of Gaza's population)</p>
              <p className="text-sm text-gray-600 mt-2">Living in tents, schools, ruins</p>
            </div>

            <div className="bg-yellow-50 rounded-xl p-6 border-l-4 border-yellow-600">
              <div className="flex items-center gap-3 mb-4">
                <i className="ri-restaurant-line text-3xl text-yellow-600"></i>
                <h3 className="text-xl font-bold text-gray-900">Famine</h3>
              </div>
              <p className="text-4xl font-bold text-yellow-600 mb-2">100%</p>
              <p className="text-gray-700">Of Gaza's population facing food insecurity</p>
              <p className="text-sm text-gray-600 mt-2">Children dying of starvation</p>
            </div>

            <div className="bg-blue-50 rounded-xl p-6 border-l-4 border-blue-600">
              <div className="flex items-center gap-3 mb-4">
                <i className="ri-hospital-line text-3xl text-blue-600"></i>
                <h3 className="text-xl font-bold text-gray-900">Healthcare</h3>
              </div>
              <p className="text-4xl font-bold text-blue-600 mb-2">36/36</p>
              <p className="text-gray-700">Hospitals destroyed or non-functional</p>
              <p className="text-sm text-gray-600 mt-2">No medical care available</p>
            </div>
          </div>
        </section>

        {/* What Women Face */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold mb-8 text-gray-900">What Palestinian Women Are Facing Right Now</h2>
          
          <div className="space-y-8">
            <div className="bg-pink-50 rounded-xl p-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-3">
                <i className="ri-parent-line text-pink-600 text-3xl"></i>
                Giving Birth in Hell
              </h3>
              <div className="prose prose-lg text-gray-700 space-y-4">
                <p>
                  <strong>52,000+ pregnant women in Gaza right now.</strong> Most will give birth:
                </p>
                <ul className="space-y-2 ml-6">
                  <li>In tents with no medical equipment</li>
                  <li>With no pain relief, no anesthesia</li>
                  <li>With no clean water to wash the baby</li>
                  <li>With no diapers, no formula, no baby clothes</li>
                  <li>While bombs fall around them</li>
                </ul>
                <p className="text-pink-700 font-semibold">
                  Imagine bringing a baby into the world knowing you can't feed them. Knowing they might not survive their first week.
                </p>
              </div>
            </div>

            <div className="bg-purple-50 rounded-xl p-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-3">
                <i className="ri-heart-pulse-line text-purple-600 text-3xl"></i>
                No Menstrual Products, No Privacy, No Dignity
              </h3>
              <div className="prose prose-lg text-gray-700 space-y-4">
                <p>
                  <strong>1 million women and girls</strong> are menstruating without:
                </p>
                <ul className="space-y-2 ml-6">
                  <li>Pads or tampons (none available for 14 months)</li>
                  <li>Clean water to wash</li>
                  <li>Privacy (living in crowded tents with 20+ people)</li>
                  <li>Clean clothes to change into</li>
                  <li>Pain medication for cramps</li>
                </ul>
                <p className="text-purple-700 font-semibold">
                  Women are using torn fabric, cardboard, anything they can find. They're developing infections. They're in pain. And they have nowhere private to deal with it.
                </p>
              </div>
            </div>

            <div className="bg-red-50 rounded-xl p-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-3">
                <i className="ri-shield-cross-line text-red-600 text-3xl"></i>
                Sexual Violence and Detention
              </h3>
              <div className="prose prose-lg text-gray-700 space-y-4">
                <p>
                  <strong>Documented reports</strong> of Palestinian women:
                </p>
                <ul className="space-y-2 ml-6">
                  <li>Stripped and humiliated at checkpoints</li>
                  <li>Sexually assaulted in detention</li>
                  <li>Forced to give birth while handcuffed</li>
                  <li>Separated from their children during raids</li>
                  <li>Detained without charge, without access to lawyers</li>
                </ul>
                <p className="text-red-700 font-semibold">
                  These are war crimes. But with no electricity, no internet, no journalists—who's documenting them? Who's holding anyone accountable?
                </p>
              </div>
            </div>

            <div className="bg-gray-50 rounded-xl p-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-3">
                <i className="ri-parent-fill text-gray-600 text-3xl"></i>
                Watching Their Children Starve
              </h3>
              <div className="prose prose-lg text-gray-700 space-y-4">
                <p>
                  <strong>The worst torture for any mother:</strong>
                </p>
                <ul className="space-y-2 ml-6">
                  <li>Children crying "Mama, I'm hungry" and having nothing to give them</li>
                  <li>Babies losing weight, becoming skeletal</li>
                  <li>Toddlers too weak to walk</li>
                  <li>Watching your child's hair fall out from malnutrition</li>
                  <li>Knowing your child might die—not from bombs, but from hunger</li>
                </ul>
                <p className="text-gray-700 font-semibold">
                  Aid trucks are blocked. Food is used as a weapon. Mothers are forced to watch their children waste away.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* What Children Face */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold mb-8 text-gray-900">What Palestinian Children Are Living Through</h2>
          
          <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-8 md:p-12 mb-8">
            <div className="grid md:grid-cols-3 gap-6 mb-8">
              <div className="text-center">
                <p className="text-5xl font-bold text-red-600 mb-2">17,000+</p>
                <p className="text-gray-700 font-semibold">Children killed</p>
              </div>
              <div className="text-center">
                <p className="text-5xl font-bold text-orange-600 mb-2">25,000+</p>
                <p className="text-gray-700 font-semibold">Children orphaned</p>
              </div>
              <div className="text-center">
                <p className="text-5xl font-bold text-yellow-600 mb-2">625,000</p>
                <p className="text-gray-700 font-semibold">Out of school</p>
              </div>
            </div>
            
            <div className="space-y-6 text-gray-700">
              <p className="text-lg">
                <strong>Children in Gaza are:</strong>
              </p>
              <ul className="space-y-3 ml-6 text-lg">
                <li className="flex items-start gap-3">
                  <i className="ri-arrow-right-s-fill text-indigo-600 text-xl mt-1"></i>
                  <span><strong>Living in tents</strong> - No heat in winter, no protection from rain, sleeping on concrete</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-arrow-right-s-fill text-indigo-600 text-xl mt-1"></i>
                  <span><strong>Starving</strong> - 90% of children under 2 face severe food poverty, many dying of malnutrition</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-arrow-right-s-fill text-indigo-600 text-xl mt-1"></i>
                  <span><strong>Traumatized</strong> - Every child has witnessed death, many have lost parents, siblings, friends</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-arrow-right-s-fill text-indigo-600 text-xl mt-1"></i>
                  <span><strong>Sick</strong> - Diseases spreading (hepatitis, polio, respiratory infections) with no medicine</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-arrow-right-s-fill text-indigo-600 text-xl mt-1"></i>
                  <span><strong>Losing childhood</strong> - No school, no play, no safety, no future</span>
                </li>
              </ul>
              <p className="text-indigo-700 font-semibold text-xl mt-6">
                An entire generation is being destroyed. And the world has stopped watching.
              </p>
            </div>
          </div>
        </section>

        {/* Why Media Silence */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold mb-8 text-gray-900">Why the Media Stopped Covering Gaza</h2>
          
          <div className="bg-gray-900 text-white rounded-2xl p-8 md:p-12">
            <div className="space-y-6 text-lg">
              <p>
                <strong>It's not because the crisis ended. It's because:</strong>
              </p>
              <div className="space-y-4 ml-6">
                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 rounded-full bg-red-600 flex items-center justify-center flex-shrink-0 mt-1">
                    <span className="font-bold">1</span>
                  </div>
                  <div>
                    <p className="font-semibold mb-2">No Electricity = No Content</p>
                    <p className="text-gray-300">Without power, people can't charge phones, can't post videos, can't document what's happening. No viral videos = no media attention.</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 rounded-full bg-red-600 flex items-center justify-center flex-shrink-0 mt-1">
                    <span className="font-bold">2</span>
                  </div>
                  <div>
                    <p className="font-semibold mb-2">Journalists Can't Work</p>
                    <p className="text-gray-300">130+ journalists killed—the deadliest conflict for media workers ever. Those still alive can't file reports without internet or electricity.</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 rounded-full bg-red-600 flex items-center justify-center flex-shrink-0 mt-1">
                    <span className="font-bold">3</span>
                  </div>
                  <div>
                    <p className="font-semibold mb-2">News Cycle Moved On</p>
                    <p className="text-gray-300">Media covers what's "new." After 14 months, Gaza isn't "news" anymore—even though people are still dying every day.</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 rounded-full bg-red-600 flex items-center justify-center flex-shrink-0 mt-1">
                    <span className="font-bold">4</span>
                  </div>
                  <div>
                    <p className="font-semibold mb-2">Political Pressure</p>
                    <p className="text-gray-300">Some media outlets face pressure not to cover Gaza, or to frame it in specific ways. Critical coverage is labeled "biased."</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 rounded-full bg-red-600 flex items-center justify-center flex-shrink-0 mt-1">
                    <span className="font-bold">5</span>
                  </div>
                  <div>
                    <p className="font-semibold mb-2">Compassion Fatigue</p>
                    <p className="text-gray-300">Audiences get tired of "depressing" news. Media follows ratings. Gaza doesn't get clicks anymore, so it doesn't get coverage.</p>
                  </div>
                </div>
              </div>
              
              <p className="text-yellow-300 font-semibold text-xl mt-8 border-t border-gray-700 pt-6">
                But just because you're not seeing it doesn't mean it's not happening. The blackout is intentional. The silence is strategic. And it's working.
              </p>
            </div>
          </div>
        </section>

        {/* What You Can Do */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold mb-8 text-gray-900">What You Can Do Right Now</h2>
          
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-green-50 rounded-xl p-6 border-l-4 border-green-600">
              <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-3">
                <i className="ri-hand-heart-line text-2xl text-green-600"></i>
                Donate
              </h3>
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-fill text-green-600 mt-1"></i>
                  <div>
                    <strong>UNRWA:</strong> <a href="https://www.unrwa.org" target="_blank" rel="noopener noreferrer" className="text-green-700 hover:underline">unrwa.org</a>
                    <p className="text-sm text-gray-600">UN agency providing food, shelter, healthcare</p>
                  </div>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-fill text-green-600 mt-1"></i>
                  <div>
                    <strong>Medical Aid for Palestinians:</strong> <a href="https://www.map.org.uk" target="_blank" rel="noopener noreferrer" className="text-green-700 hover:underline">map.org.uk</a>
                    <p className="text-sm text-gray-600">Emergency medical care and supplies</p>
                  </div>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-fill text-green-600 mt-1"></i>
                  <div>
                    <strong>Palestine Children's Relief Fund:</strong> <a href="https://www.pcrf.net" target="_blank" rel="noopener noreferrer" className="text-green-700 hover:underline">pcrf.net</a>
                    <p className="text-sm text-gray-600">Medical care for injured children</p>
                  </div>
                </li>
              </ul>
            </div>

            <div className="bg-blue-50 rounded-xl p-6 border-l-4 border-blue-600">
              <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-3">
                <i className="ri-megaphone-line text-2xl text-blue-600"></i>
                Advocate
              </h3>
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-fill text-blue-600 mt-1"></i>
                  <span><strong>Call your representatives</strong> - Demand ceasefire, humanitarian aid</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-fill text-blue-600 mt-1"></i>
                  <span><strong>Sign petitions</strong> - Amnesty International, Human Rights Watch</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-fill text-blue-600 mt-1"></i>
                  <span><strong>Boycott complicit companies</strong> - BDS movement guidelines</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-fill text-blue-600 mt-1"></i>
                  <span><strong>Attend protests</strong> - Show solidarity, demand action</span>
                </li>
              </ul>
            </div>

            <div className="bg-purple-50 rounded-xl p-6 border-l-4 border-purple-600">
              <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-3">
                <i className="ri-share-line text-2xl text-purple-600"></i>
                Amplify
              </h3>
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-fill text-purple-600 mt-1"></i>
                  <span><strong>Share Palestinian voices</strong> - Follow and amplify Gaza journalists, activists</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-fill text-purple-600 mt-1"></i>
                  <span><strong>Post about Gaza</strong> - Keep it visible on social media</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-fill text-purple-600 mt-1"></i>
                  <span><strong>Share this article</strong> - Help others understand what's happening</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-fill text-purple-600 mt-1"></i>
                  <span><strong>Correct misinformation</strong> - Combat propaganda with facts</span>
                </li>
              </ul>
            </div>

            <div className="bg-orange-50 rounded-xl p-6 border-l-4 border-orange-600">
              <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-3">
                <i className="ri-book-open-line text-2xl text-orange-600"></i>
                Educate Yourself
              </h3>
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-fill text-orange-600 mt-1"></i>
                  <span><strong>Learn the history</strong> - Understand the context of this crisis</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-fill text-orange-600 mt-1"></i>
                  <span><strong>Follow credible sources</strong> - Al Jazeera, Middle East Eye, +972 Magazine</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-fill text-orange-600 mt-1"></i>
                  <span><strong>Read Palestinian authors</strong> - Understand their perspective</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-fill text-orange-600 mt-1"></i>
                  <span><strong>Question mainstream narratives</strong> - Think critically about what you're told</span>
                </li>
              </ul>
            </div>
          </div>
        </section>

        {/* Why SheCan Cares */}
        <section className="mb-16">
          <div className="bg-gradient-to-br from-red-600 to-pink-600 text-white rounded-2xl p-8 md:p-12">
            <h2 className="text-3xl font-bold mb-6">Why SheCan Will Never Stop Talking About Palestine</h2>
            <div className="space-y-4 text-lg">
              <p>
                <strong>Because women's rights are universal.</strong> We can't fight for women in Luxembourg and ignore women in Gaza. We can't say "all women matter" and then be silent when Palestinian women are suffering.
              </p>
              <p>
                <strong>Because silence is complicity.</strong> When we stop talking about Gaza, we're telling the world it's okay to forget. It's okay to let people suffer in the dark. It's not okay.
              </p>
              <p>
                <strong>Because Palestinian women are our sisters.</strong> Their pain is our pain. Their struggle is our struggle. Their freedom is tied to ours.
              </p>
              <p>
                <strong>Because the media won't do it.</strong> If mainstream outlets won't cover Gaza, then we will. If journalists can't report from there, we'll amplify the voices that do make it out.
              </p>
              <p className="text-2xl font-bold mt-8 pt-6 border-t border-white/30">
                SheCan stands with Palestine. Today. Tomorrow. Until Palestine is free. 🇵🇸💜
              </p>
            </div>
          </div>
        </section>

        {/* Resources */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold mb-8 text-gray-900">Resources & Organizations</h2>
          
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-gray-50 rounded-xl p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Emergency Aid</h3>
              <ul className="space-y-3 text-gray-700">
                <li>
                  <a href="https://www.unrwa.org" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline font-semibold">UNRWA</a>
                  <p className="text-sm">UN Relief and Works Agency</p>
                </li>
                <li>
                  <a href="https://www.map.org.uk" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline font-semibold">Medical Aid for Palestinians</a>
                  <p className="text-sm">Emergency medical care</p>
                </li>
                <li>
                  <a href="https://www.pcrf.net" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline font-semibold">Palestine Children's Relief Fund</a>
                  <p className="text-sm">Children's medical care</p>
                </li>
                <li>
                  <a href="https://www.islamic-relief.org" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline font-semibold">Islamic Relief</a>
                  <p className="text-sm">Food, water, shelter</p>
                </li>
              </ul>
            </div>

            <div className="bg-gray-50 rounded-xl p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Advocacy & Human Rights</h3>
              <ul className="space-y-3 text-gray-700">
                <li>
                  <a href="https://www.amnesty.org/en/location/middle-east-and-north-africa/palestine-state-of/report-palestine-state-of/" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline font-semibold">Amnesty International</a>
                  <p className="text-sm">Human rights documentation</p>
                </li>
                <li>
                  <a href="https://www.hrw.org/middle-east/north-africa/israel/palestine" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline font-semibold">Human Rights Watch</a>
                  <p className="text-sm">War crimes investigation</p>
                </li>
                <li>
                  <a href="https://bdsmovement.net" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline font-semibold">BDS Movement</a>
                  <p className="text-sm">Boycott, Divestment, Sanctions</p>
                </li>
                <li>
                  <a href="https://www.amnesty.lu/agir/petitions/" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline font-semibold">Amnesty Luxembourg Petitions</a>
                  <p className="text-sm">Sign petitions for Palestine</p>
                </li>
              </ul>
            </div>

            <div className="bg-gray-50 rounded-xl p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-4">News & Information</h3>
              <ul className="space-y-3 text-gray-700">
                <li>
                  <a href="https://www.aljazeera.com/where/palestine/" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline font-semibold">Al Jazeera</a>
                  <p className="text-sm">Live coverage from Gaza</p>
                </li>
                <li>
                  <a href="https://www.middleeasteye.net/countries/palestine" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline font-semibold">Middle East Eye</a>
                  <p className="text-sm">Independent journalism</p>
                </li>
                <li>
                  <a href="https://www.972mag.com" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline font-semibold">+972 Magazine</a>
                  <p className="text-sm">Israeli-Palestinian journalism</p>
                </li>
                <li>
                  <a href="https://mondoweiss.net" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline font-semibold">Mondoweiss</a>
                  <p className="text-sm">News and analysis</p>
                </li>
              </ul>
            </div>

            <div className="bg-gray-50 rounded-xl p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Palestinian Voices</h3>
              <ul className="space-y-3 text-gray-700">
                <li>
                  <span className="font-semibold">@eye.on.palestine</span>
                  <p className="text-sm">Instagram - Gaza documentation</p>
                </li>
                <li>
                  <span className="font-semibold">@motaz_azaiza</span>
                  <p className="text-sm">Instagram - Photojournalist from Gaza</p>
                </li>
                <li>
                  <span className="font-semibold">@wizard_bisan1</span>
                  <p className="text-sm">Instagram - Daily life in Gaza</p>
                </li>
                <li>
                  <span className="font-semibold">@gazagirl.mp3</span>
                  <p className="text-sm">TikTok - Young woman's perspective</p>
                </li>
              </ul>
            </div>
          </div>
        </section>

        {/* Final Message */}
        <section className="mb-16">
          <div className="bg-black text-white rounded-2xl p-8 md:p-12 text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">Don't Let Them Disappear</h2>
            <p className="text-xl mb-8 text-gray-300">
              The blackout is intentional. The silence is strategic. But we can break it.
            </p>
            <p className="text-2xl font-bold text-red-400 mb-8">
              Keep talking about Gaza. Keep sharing. Keep caring. Keep fighting.
            </p>
            <p className="text-3xl font-bold">
              🇵🇸 Free Palestine 🇵🇸
            </p>
          </div>
        </section>

        {/* Share */}
        <div className="flex flex-wrap gap-4 justify-center pt-8 border-t border-gray-200">
          <button className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2 whitespace-nowrap cursor-pointer">
            <i className="ri-facebook-fill text-xl"></i>
            Share on Facebook
          </button>
          <button className="px-6 py-3 bg-black text-white rounded-lg hover:bg-gray-800 transition-colors flex items-center gap-2 whitespace-nowrap cursor-pointer">
            <i className="ri-twitter-x-fill text-xl"></i>
            Share on X
          </button>
          <button className="px-6 py-3 bg-pink-600 text-white rounded-lg hover:bg-pink-700 transition-colors flex items-center gap-2 whitespace-nowrap cursor-pointer">
            <i className="ri-instagram-fill text-xl"></i>
            Share on Instagram
          </button>
          <button className="px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2 whitespace-nowrap cursor-pointer">
            <i className="ri-whatsapp-fill text-xl"></i>
            Share on WhatsApp
          </button>
        </div>

      </article>

      <Footer />
    </div>
  );
}